import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, share } from 'rxjs';
import { environment } from '@pt/environment';
import { excludeEmptyIds, getQueryParams } from '@hglobal/common';
import { AdminFinancialRequest, AdminFinancialRequestAssigneeCategory } from '@pt/models';

import {
  ChangeRequestStatusReq,
  ChangeRequestStatusRes,
  CreateRequestReq,
  CreateRequestRes,
  GetFinancialCategoriesRes,
  GetLocationsRes,
  GetRequestDetailsEncryptedDataReq,
  GetRequestDetailsEncryptedDataRes,
  GetRequestDetailsReq,
  GetRequestDetailsRes,
  GetRequestIdHashRes,
  GetRequestsReq,
  GetRequestsRes,
  GetUsersByIdsReq,
  GetUsersByIdsRes,
  SaveRequestEncryptedData,
} from '../models';
import { FinancialRequestsApi } from './financial-requests-api.interface';
import { GetAssigneesRes } from '../models/get-assignees-res.model';
import { GetTeamsRes } from '../models/get-teams-res.model';
import { GetFunctionsRes } from '../models/get-functions-res.model';
import { GetServicesRes } from '../models/get-services-res.model';
import { GetBusinessUnitsRes } from '../models/get-business-units-res.model';

@Injectable()
export class UserFinancialRequestsService implements FinancialRequestsApi {
  readonly #httpClient = inject(HttpClient);

  getRequestIdHash(): Observable<GetRequestIdHashRes> {
    return this.#httpClient.get<GetRequestIdHashRes>(
      `${environment.apiHost}support/financial-requests/generate-hash/`,
    ).pipe(share());
  }

  createRequest(params: CreateRequestReq): Observable<CreateRequestRes> {
    const payload = {
      requestType: params.requestType,
      requestIdHash: params.requestIdHash,
      categoryId: params.categoryId,
    };

    return this.#httpClient.post<CreateRequestRes>(
      `${environment.apiHost}support/financial-requests/`,
      excludeEmptyIds(payload),
    ).pipe(share());
  }

  patchRequestAssigneeCategory(
    requestId: number,
    params: AdminFinancialRequestAssigneeCategory,
  ): Observable<AdminFinancialRequest> {
    return this.#httpClient.patch<any>(
      `${environment.apiHost}support/admin/financial-requests/${requestId}/assignee-category/`,
      params,
    ).pipe(share());
  }

  saveRequestEncryptedData(
    params: SaveRequestEncryptedData,
  ): Observable<SaveRequestEncryptedData> {
    return this.#httpClient.post<SaveRequestEncryptedData>(
      `${environment.apiHost}financial-requests/${params.requestIdHash}/messages/`,
      excludeEmptyIds(params),
    ).pipe(share());
  }

  getRequests(params: GetRequestsReq, isAssignedToMe: boolean): Observable<GetRequestsRes> {
    return this.#httpClient.get<GetRequestsRes>(
      `${environment.apiHost}support/financial-requests/`,
      { params: getQueryParams(params) },
    ).pipe(share());
  }

  getLocations(): Observable<GetLocationsRes> {
    return this.#httpClient.get<GetLocationsRes>(
      `${environment.apiHost}user/select-options/locations/`,
    ).pipe(share());
  }

  getTeams(): Observable<GetTeamsRes> {
    return this.#httpClient.get<GetTeamsRes>(
      `${environment.apiHost}user/select-options/teams/`,
    ).pipe(share());
  }

  getFunctions(): Observable<GetFunctionsRes> {
    return this.#httpClient.get<GetFunctionsRes>(
      `${environment.apiHost}user/select-options/functions/`,
    ).pipe(share());
  }

  getServices(): Observable<GetServicesRes> {
    return this.#httpClient.get<GetServicesRes>(
      `${environment.apiHost}user/select-options/services/`,
    ).pipe(share());
  }

  getBusinessUnits(): Observable<GetBusinessUnitsRes> {
    return this.#httpClient.get<GetBusinessUnitsRes>(
      `${environment.apiHost}user/select-options/business-units/`,
    ).pipe(share());
  }

  getFinancialCategories(): Observable<GetFinancialCategoriesRes> {
    return this.#httpClient.get<GetFinancialCategoriesRes>(
      `${environment.apiHost}support/financial-request-categories/`,
    ).pipe(share());
  }

  getAssignees(): Observable<GetAssigneesRes> {
    return this.#httpClient.get<GetAssigneesRes>(
      `${environment.apiHost}support/admin/financial-request-assignees/available/`,
    ).pipe(share());
  }

  getRequestDetails(params: GetRequestDetailsReq): Observable<GetRequestDetailsRes> {
    return this.#httpClient.get<GetRequestDetailsRes>(
      `${environment.apiHost}support/financial-requests/${params.requestId}/`,
    ).pipe(share());
  }

  getRequestDetailsEncryptedData(
    params: GetRequestDetailsEncryptedDataReq,
  ): Observable<GetRequestDetailsEncryptedDataRes> {
    return this.#httpClient.get<GetRequestDetailsEncryptedDataRes>(
      `${environment.apiHost}financial-requests/${params.requestIdHash}/`,
    ).pipe(share());
  }

  getUsersByIds(params: GetUsersByIdsReq): Observable<GetUsersByIdsRes> {
    return this.#httpClient.get<GetUsersByIdsRes>(
      `${environment.apiHost}user/users-search/`, { params: getQueryParams(params) },
    ).pipe(share());
  }

  changeRequestStatus(params: ChangeRequestStatusReq): Observable<ChangeRequestStatusRes> {
    const payload = { status: params.status };

    return this.#httpClient.post<ChangeRequestStatusRes>(
      `${environment.apiHost}support/financial-requests/${params.requestId}/change-status/`,
      excludeEmptyIds(payload),
    ).pipe(share());
  }

  downloadReport(
    params: GetRequestsReq,
    isAssignedToMe: boolean,
  ): Observable<HttpResponse<Blob>> {
    delete params.page;
    delete params.limit;

    return this.#httpClient.get<Blob>(
      // eslint-disable-next-line @stylistic/max-len
      `${environment.apiHost}support/admin/financial-requests${isAssignedToMe ? '/assigned' : ''}/download-report/`,
      {
        params: getQueryParams(params),
        responseType: 'blob' as 'json',
        observe: 'response',
      },
    );
  }
}
